// ✅ PlanEditor.jsx — PMIS UI + TableCell 통합 버전
import React, { useEffect, useMemo, useState, useRef } from "react";
import TableCell from "../../common/ui/TableCell";
import TinyMCEeditor from "../../const/TinyMCEeditor";
import AttachmentBox from "../../common/papers/AttachmentBox";

export default function PlanEditor({
  initial = {},
  onChange,
  readOnly = false,
  currentUser = {},
}) {
  const editorRef = useRef(null);
  const attachmentRef = useRef(null);

  const today = new Date().toISOString().slice(0, 10);

  // 사용자 안전 처리
  const safeUser =
    typeof currentUser === "string"
      ? (() => {
          try {
            return JSON.parse(currentUser);
          } catch {
            return {};
          }
        })()
      : currentUser || {};

  // ==========================
  // FORM STATE
  // ==========================
  const [form, setForm] = useState({
    doc_date: initial.doc_date || today,
    status: initial.status || "draft",
    title: initial.title || "",
    author: initial.author || safeUser.name || "",
    version: initial.version || "1.0",
    note: initial.note || "",
  });

  const onForm = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  // 자동 제목 생성
  const autoTitle = useMemo(() => {
    const v = form.version?.trim();
    const date = form.doc_date?.replace(/-/g, "");

    if (v) return `[REPORT] 안전관리 계획서 v${v} (${date})`;
    return `[REPORT] 안전관리 계획서 (${date})`;
  }, [form.version, form.doc_date]);

  // ==========================
  // SafetyForm(onChange)에 맞추기
  // ==========================
  useEffect(() => {
    if (!onChange) return;

    // ✅ 저장/표시용 최종 제목(빈 값이면 자동 제목 사용)
    const resolvedTitle = form.title?.trim() || autoTitle;

    const meta = {
      doc_date: form.doc_date || today,
      author: form.author,
      version: form.version,
      note: form.note,
      status: form.status, // ✅ 저장/표시 통일용
    };

    const summary = {
      title: resolvedTitle,
      requestDept: "-",
      reason: form.note,
      details: [
        { label: "작성자", value: form.author },
        { label: "버전", value: form.version },
        { label: "작성일", value: form.doc_date },
      ],
      attachments: attachmentRef.current?.files || [],
    };

    onChange({
      title: resolvedTitle,
      meta,
      summary,
    });
  }, [form, autoTitle, onChange]);

  // ==========================
  // UI RENDER
  // ==========================
  return (
    <div>
      {/* ---------------- 기본 정보 ---------------- */}
      <section>
        <table className="w-full text-sm">
          <tbody>
            {/* 제목 */}
            <tr>
              <TableCell
                label="제목"
                editing={!readOnly}
                colSpan={3}
                value={form.title}
                input={
                  <>
                    <input
                      className="input-base w-full"
                      placeholder={autoTitle}
                      value={form.title}
                      onChange={(e) => onForm("title", e.target.value)}
                      disabled={readOnly}
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      미입력 시 자동 제목: {autoTitle}
                    </p>
                  </>
                }
              />
            </tr>

            {/* 버전 + 상태 */}
            <tr>
              <TableCell
                label="버전"
                editing={!readOnly}
                value={form.version}
                input={
                  <input
                    className="input-base w-full"
                    value={form.version}
                    onChange={(e) => onForm("version", e.target.value)}
                    disabled={readOnly}
                  />
                }
              />

              <TableCell
                label="상태"
                editing={false}
                value={
                  form.status === "draft"
                    ? "임시 저장"
                    : form.status === "approved"
                    ? "승인"
                    : form.status === "rejected"
                    ? "반려"
                    : form.status === "submit"
                    ? "상신"
                    : form.status === "cancel"
                    ? "취소"
                    : form.status === "none"
                    ? "-"
                    : "진행중"
                }
              />
            </tr>

            {/* 작성일 + 작성자 */}
            <tr>
              <TableCell label="작성일" value={form.doc_date} editing={false} />

              <TableCell label="작성자" value={form.author} editing={false} />
            </tr>

            {/* 비고 */}
            <tr>
              <TableCell
                label="비고"
                colSpan={3}
                editing={!readOnly}
                value={form.note}
                rich
                input={
                  <TinyMCEeditor
                    initialValue={form.note}
                    editorRef={editorRef}
                    onChange={(v) => onForm("note", v)}
                  />
                }
              />
            </tr>
          </tbody>
        </table>
      </section>
    </div>
  );
}
